/**
 * The words game text highlights, and what each one says in its tip, built
 * from the keyword glossary and the status effect registry.
 *
 * Built on first use, not on import: main.ts imports the app before it
 * imports the status effect index that fills the registry, so anything
 * computed at import time would see no status effects at all.
 */
import { keywords } from '../models/keywords';
import { StatusEffectRegistry, listStatusEffects } from '../models/status-effects';
import { createTokeniser, type Tokeniser } from './text-tokens';
import { ModifierTags } from '../models/modifier.type';

export type EffectInfo = {
  title: string;
  text: string;
  icon: string;
  /** Whether it helps or harms whoever carries it, when it says. */
  polarity: ModifierTags.Boon | ModifierTags.Bane | null;
};

let effects: Record<string, EffectInfo> | null = null;
const tokenisers: { withKeywords?: Tokeniser; withoutKeywords?: Tokeniser } = {};

/** Title, text and icon for every registered status effect, by type. */
export function statusEffectInfo(): Record<string, EffectInfo> {
  if (effects) return effects;

  const found: Record<string, EffectInfo> = {};
  for (const type of listStatusEffects()) {
    const Effect = StatusEffectRegistry.get(type);
    if (!Effect) continue;
    const instance = new Effect();
    const tags: Array<string> = instance.tags ?? [];
    found[type] = {
      title: instance.title,
      text: instance.text,
      icon: instance.icon,
      polarity: tags.includes(ModifierTags.Boon)
        ? ModifierTags.Boon
        : tags.includes(ModifierTags.Bane)
          ? ModifierTags.Bane
          : null,
    };
  }

  // Not cached until the registry has filled, so an early call is not
  // remembered as "there are no status effects".
  if (Object.keys(found).length) effects = found;
  return found;
}

export function keywordText(key: string): string | undefined {
  return keywords[key];
}

/** A tokeniser over the game's words, with or without the keywords. */
export function gameTokeniser(includeKeywords: boolean): Tokeniser {
  const slot = includeKeywords ? 'withKeywords' : 'withoutKeywords';
  const cached = tokenisers[slot];
  if (cached) return cached;

  const info = statusEffectInfo();
  const types = Object.keys(info);

  // An effect registered under a different name from its title -- Poison is
  // 'poisoned' -- is found by its title too.
  const aliases: Record<string, string> = {};
  for (const type of types) {
    const title = info[type].title?.toLowerCase();
    if (title && title !== type) aliases[title] = type;
  }

  const tokeniser = createTokeniser({
    keywords: includeKeywords ? Object.keys(keywords) : [],
    effects: types,
    aliases,
  });

  if (types.length) tokenisers[slot] = tokeniser;
  return tokeniser;
}
